import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { meses, relatorios, withAuthRetry } from '../lib/api';
import { useAuth } from './AuthContext';
import { canNavigateToMonth } from '../utils/planLimits';

const MonthContext = createContext(null);

export function MonthProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const plan = user?.plan || 'gratuito';
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { mes: now.getMonth() + 1, ano: now.getFullYear() };
  });
  const [mesData, setMesData] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load month data when month changes
  const loadMonthData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const mesResponse = await withAuthRetry(() =>
        meses.getOrCreate(currentMonth.mes, currentMonth.ano)
      );
      setMesData(mesResponse);

      const resumoResponse = await withAuthRetry(() =>
        relatorios.getResumoMensal(mesResponse.id)
      );
      setResumo(resumoResponse);
    } catch (error) {
      console.error('Failed to load month data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentMonth.mes, currentMonth.ano]);

  useEffect(() => {
    loadMonthData();
  }, [loadMonthData]);

  const prevMonth = useMemo(() => {
    if (currentMonth.mes === 1) return { mes: 12, ano: currentMonth.ano - 1 };
    return { mes: currentMonth.mes - 1, ano: currentMonth.ano };
  }, [currentMonth]);

  const nextMonth = useMemo(() => {
    if (currentMonth.mes === 12) return { mes: 1, ano: currentMonth.ano + 1 };
    return { mes: currentMonth.mes + 1, ano: currentMonth.ano };
  }, [currentMonth]);

  const canGoPrevious = canNavigateToMonth(plan, prevMonth.mes, prevMonth.ano);
  const canGoNext = canNavigateToMonth(plan, nextMonth.mes, nextMonth.ano);

  const goToPreviousMonth = () => {
    if (!canGoPrevious) return;
    setCurrentMonth(prevMonth);
  };

  const goToNextMonth = () => {
    if (!canGoNext) return;
    setCurrentMonth(nextMonth);
  };

  const goToMonth = (mes, ano) => {
    if (!canNavigateToMonth(plan, mes, ano)) return;
    setCurrentMonth({ mes, ano });
  };

  const refreshData = useCallback(() => {
    loadMonthData();
  }, [loadMonthData]);

  const value = {
    currentMonth,
    mesData,
    resumo,
    loading,
    goToPreviousMonth,
    goToNextMonth,
    goToMonth,
    refreshData,
    canGoPrevious,
    canGoNext,
  };

  return (
    <MonthContext.Provider value={value}>
      {children}
    </MonthContext.Provider>
  );
}

export function useMonth() {
  const context = useContext(MonthContext);
  if (!context) {
    throw new Error('useMonth must be used within a MonthProvider');
  }
  return context;
}

export default MonthContext;
